import type { ReactNode } from "react";
import { useBottomToastRef } from "@/components/FloatingPillRail";

/**
 * #5114 TOASTCROSBOT: the one fixed, bottom-centred toast. UpdateToast (#769),
 * NoticeToast (#3072) and the Knowledge Base undo bar (#3716) each copied this
 * markup and anchor, so on a phone all three sat on Ask Crosbot. Each keeps
 * its own content and buttons; this carries the box, the anchor and the lift.
 *
 * The anchor is 1.25rem plus the safe-area inset at every width, as before.
 * The element goes to the pill rail (useBottomToastRef), which below lg sets
 * data-lifted when the toast's span across the screen meets the rail's
 * (toastMeetsRail); then the toast stands on the pills' band, 8 px above
 * them. It counts every toast in --pill-rail-band below lg, so a page's last
 * row still scrolls clear. Outside the rail's provider (Display Mode) the
 * toast keeps its own offset.
 *
 * Two of these showing at once still sit on one spot, as before #5114.
 */
export const BOTTOM_TOAST_CLASS =
  "fixed left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm text-card-foreground shadow-lg bottom-[calc(1.25rem+env(safe-area-inset-bottom,0px))] max-lg:data-lifted:bottom-[max(calc(1.25rem+env(safe-area-inset-bottom,0px)),var(--pill-rail-pills,0px))]";

/** A 44 px tap target below lg for a button on a bottom toast; from lg it keeps its size. */
export const BOTTOM_TOAST_TAP = "min-h-11 min-w-11 lg:min-h-0 lg:min-w-0";

export function BottomToast({ children, testId }: { children: ReactNode; testId?: string }) {
  const toastRef = useBottomToastRef();
  return (
    <div ref={toastRef ?? undefined} role="status" aria-live="polite" data-testid={testId} className={BOTTOM_TOAST_CLASS}>
      {children}
    </div>
  );
}
